<template>
  <div class="box">
    <div
      class="modal"
      :class="{ 'is-active': modal }"
    >
      <div class="modal-background" />
      <div class="modal-content">
        <div class="box is-centered">
          <div class="field">
            <div class="label">
              ¿Estas seguro de eliminar esta factura?
            </div>
            <div class="control is-centered">
              <div class="field is-grouped">
                <p class="control">
                  <a
                    class="button is-success"
                    :class="{ 'is-loading': isLoading }"
                    :disabled="isLoading"
                    @click="deleteById"
                  >
                    <span class="icon is-smal">
                      <i class="material-icons">
                        check
                      </i>
                    </span>
                    <span>
                      Sí
                    </span>
                  </a>
                </p>
                <p class="control">
                  <a
                    class="button is-danger"
                    :class="{ 'is-loading': isLoading }"
                    :disabled="isLoading"
                    @click="onCloseModal"
                  >
                    <span class="icon is-smal">
                      <i class="material-icons">
                        clear
                      </i>
                    </span>
                    <span>
                      No
                    </span>
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <button
        class="modal-close is-large"
        aria-label="close"
        @click="onCloseModal"
      />
    </div>
    <h1 class="title is-4 has-text-primary">
      Pre Facturas
    </h1>
    <AppPagination
      class="is-centered"
      :current-page="currentPage"
      :buttons-max="buttonsMax"
      :pages-total="totalPages"
      @change="goToPage"
    />
    <table class="table is-fullwidth">
      <thead>
        <tr>
          <th>Folio</th>
          <th>Fecha</th>
          <th>Cliente</th>
          <th>Total</th>
          <th>Tipo</th>
          <th />
        </tr>
      </thead>
      <tbody>
        <tr v-if="isLoading">
          <td
            colspan="8"
            class="is-loading"
          >
            <a class="button is-loading is-fullwidth">
              Cargando..
            </a>
          </td>
        </tr>
        <template v-else>
          <tr
            v-if="invoices.length === 0"
          >
            <td
              class="empty"
              colspan="7"
            >
              Sin datos
            </td>
          </tr>
          <tr
            v-for="(invoice, index) in invoices"
            v-else
            :key="index"
          >
            <td>
              {{ invoice.voucherSeries }}
              -
              {{ invoice.voucherConsecutive }}
            </td>
            <td>{{ invoice.voucherDate | toHumanDate }}</td>
            <td>{{ invoice.receiverName }}</td>
            <td>{{ invoice.voucherTotal | toCurrency(invoice.voucherCurrency) }}</td>
            <td>{{ voucherTypeLabel(invoice.voucherType) }}</td>
            <td>
              <div>
                <AppRadialMenu
                  :size="36"
                  :item-size="48"
                  :radius="80"
                  :angle-restriction="50"
                  :rotate="-25"
                >
                  <AppRadialMenuItem
                    v-if="sendToEdit(invoice)"
                    class="tooltip is-tooltip-info has-background-info has-text-white"
                    data-tooltip="Editar pre factura"
                  >
                    <RouterLink
                      class="action-option"
                      :to="sendToEdit(invoice)"
                    >
                      <BIcon
                        icon="pencil"
                        class="action-option-icon"
                      />
                    </RouterLink>
                  </AppRadialMenuItem>
                  <AppRadialMenuItem
                    class="tooltip is-tooltip-danger has-background-danger has-text-white"
                    data-tooltip="Eliminar pre factura"
                  >
                    <a
                      class="action-option"
                      @click="deleteInvoice(invoice.id)"
                    >
                      <BIcon
                        icon="cancel"
                        class="action-option-icon"
                      />
                    </a>
                  </AppRadialMenuItem>
                </AppRadialMenu>
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
    <AppPagination
      class="is-centered"
      :current-page="currentPage"
      :buttons-max="buttonsMax"
      :pages-total="totalPages"
      @change="goToPage"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';

import { voucherType as voucherTypeCatalog } from '@machete/common-catalogs';

export default {
  name: 'PreInvoiceList',
  components: {
    AppPagination: () => import(/* webpackChunkName: "app-pagination" */ '@/components/AppPagination.vue'),
    AppRadialMenu: () => import(/* webpackChunkName: "app-radial-menu" */ '@/components/AppRadialMenu.vue'),
    AppRadialMenuItem: () => import(/* webpackChunkName: "app-radial-menu-item" */ '@/components/AppRadialMenuItem.vue'),
  },
  data() {
    return {
      isLoading: false,
      modal: false,
      buttonsMax: 5,
    };
  },
  computed: {
    ...mapState({
      total: state => state.invoices.total,
      totalPages: state => state.invoices.totalPages,
    }),
    ...mapGetters({
      invoices: 'invoices/cleanInvoices',
    }),
    voucherTypeLabel() {
      return (key) => {
        const voucherType = voucherTypeCatalog.find(type => type.key === key);

        return voucherType.value;
      };
    },
    lastPage() {
      return Math.ceil(this.invoices.length / this.itemsPerPage);
    },
    currentPage() {
      return parseInt(this.$route.query.page, 10);
    },
    size() {
      return this.$route.query.size;
    },
    sendToEdit() {
      return ({ voucherType, id }) => {
        switch (voucherType) {
          case 'I':
            return { name: 'InvoiceDetail', params: { uuid: id } };
          case 'T':
            return { name: 'TradeTransferDetail', params: { uuid: id } };
          default:
            return false;
        }
      };
    },
  },
  watch: {
    buttonsMax(newValue, oldValue) {
      if (newValue % 2) {
        return;
      }
      if (newValue < oldValue) {
        this.buttonsMax = newValue - 1;
      }
      if (newValue > oldValue) {
        this.buttonsMax = newValue + 1;
      }
    },
  },
  beforeRouteEnter(to, from, next) {
    if (to.query.page) next();
    else {
      next({ name: to.name, query: { page: 1, size: 20 } });
    }
  },
  async beforeRouteUpdate(to, from, next) {
    const { page = 1, size = 20 } = to.query;

    await this.getAll({
      filter: 'pre',
      page,
      size,
    });

    next({ query: { page, size } });
  },
  async beforeMount() {
    this.isLoading = true;
    await this.getAll({
      filter: 'pre',
      page: this.currentPage,
      size: this.size,
    });
    this.isLoading = false;
  },
  mounted() {
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.modal) {
        this.modal = false;
      }
    }, false);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', () => {});
  },
  methods: {
    ...mapActions({
      getAll: 'invoices/getAll',
      deleteByUuid: 'invoice/deleteByUuid',

    }),
    goToPage(page) {
      const { size = 20 } = this.$route.query;

      const query = {
        page,
        size,
      };

      this.$router.push({ query });
    },
    checkLastPage() {
      if (this.currentPage < this.lastPage) {
        this.$router.push({ query: { page: this.lastPage } });
      }
    },
    deleteInvoice(invoice) {
      this.invoiceToDelete = invoice;
      this.modal = true;
    },
    onCloseModal() {
      this.invoiceToDelete = '';
      this.modal = false;
    },
    async deleteById() {
      this.isLoading = true;
      try {
        await this.deleteByUuid(this.invoiceToDelete);
        this.$store.dispatch('addTemporalNotification', {
          type: 'success',
          content: 'La factura se eliminó satisfactoriamente.',
        });
        this.$router.push({ name: 'PreInvoiceList' });
        this.onCloseModal();
      } catch (error) {
        this.onCloseModal();
        this.$store.dispatch('addTemporalNotification', {
          type: 'danger',
          content: `
            Ocurrió un error al tratar de eliminar la factura. <br>
            ${error.message}
          `,
        });
      }
      this.isLoading = false;
    },
  },
};
</script>

<style scoped>
.empty {
  vertical-align: center;
  text-align: center;
}

.buttons-cell {
  max-width: 250px;
  width: 220px;
}
</style>
